import { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { motion } from 'framer-motion';
import { api, formatPKR } from '../api';
import PageHeader from '../components/PageHeader';
import ClickableStatCard from '../components/ClickableStatCard';

const COLORS = { stroke: '#8B5E3C', fill: '#D4B483' };

export default function SalesAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getSalesAnalytics().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-lumora-text/50">Loading sales analytics...</p>;

  return (
    <div>
      <PageHeader title="Sales Analytics" subtitle="Revenue insights from dispatched & delivered orders" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <ClickableStatCard label="Total Revenue" value={formatPKR(data.totalRevenue)} accent />
        <ClickableStatCard label="Average Order Value" value={formatPKR(data.avgOrderValue)} />
        <ClickableStatCard label="Products Sold" value={data.productsSold} />
        <ClickableStatCard label="Total Orders" value={data.orderCount} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <ClickableStatCard label="Today's Sales" value={formatPKR(data.todaySales)} />
        <ClickableStatCard label="Weekly Sales" value={formatPKR(data.weeklySalesTotal)} accent />
        <ClickableStatCard label="Monthly Sales" value={formatPKR(data.monthlySalesTotal)} accent />
        <ClickableStatCard label="Yearly Sales" value={formatPKR(data.yearlySalesTotal)} accent />
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <motion.div className="glass-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Daily Sales Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.dailySales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="date" stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <YAxis stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Area type="monotone" dataKey="revenue" stroke={COLORS.stroke} fill={COLORS.fill} fillOpacity={0.4} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div className="glass-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Weekly Sales</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.weeklySales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="week" stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <YAxis stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Bar dataKey="revenue" fill="#8B5E3C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <motion.div className="glass-card">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Monthly Sales</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthlySales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="month" stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <YAxis stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Bar dataKey="revenue" fill="#D4B483" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div className="glass-card">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Yearly Sales</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.yearlySales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="year" stroke="#8B5E3C" />
              <YAxis stroke="#8B5E3C" />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Bar dataKey="revenue" fill="#5D3A23" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>
    </div>
  );
}
